import type { ReactNode } from 'react';
import {
    getDateRangeBefore,
    locale,
    getDetectionCountryMap,
} from '@trqk/utils';

import { URL_PARAMS } from '../../../constants';

export const detectionCountryMap = getDetectionCountryMap();

export const filterWidth = 205;
export const filterHeight = 47;

export const columnIds = {
    timestamp: 'timestamp',
    matchReference: 'matchReference',
    duration: 'duration',
    confidence: 'confidence',
    titleFilename: 'titleFilename',
    station: 'station',
    country: 'country',
    show: 'show',
    productionCompany: 'productionCompany',
    proId: 'proId',
    publisher: 'publisher',
    writer: 'writer',
};

// These column widths should have min values that add to 930
export const columnWidthInputs = {
    [columnIds.timestamp]: {
        min: 160,
        max: 160,
    },
    [columnIds.matchReference]: {
        min: 80,
        max: 80,
    },
    [columnIds.duration]: {
        min: 65,
        max: 65,
    },
    [columnIds.confidence]: {
        min: 85,
        max: 85,
    },
    [columnIds.titleFilename]: {
        min: 80,
    },
    // `station` is actually called `channel` in the DetectionsTableItem
    [columnIds.station]: {
        min: 78,
    },
    [columnIds.country]: {
        min: 60,
        max: 60,
    },
    [columnIds.show]: {
        min: 71,
    },
    [columnIds.productionCompany]: {
        min: 75,
    },
    [columnIds.proId]: {
        min: 55,
        max: 80,
    },
    [columnIds.publisher]: {
        min: 63,
        max: 70,
    },
    [columnIds.writer]: {
        min: 58,
        max: 70,
    },
};

export const filterConfigs: Record<string, TableFilterConfig> = {
    allTracks: {
        id: 'title',
        label: locale.enUS.DETECTIONS.FILTERS.TRACKS.ALL,
        value: {
            fieldValue: null,
        },
    },
    allPublishersAndWriters: {
        id: 'publisher',
        label: locale.enUS.DETECTIONS.FILTERS.PUBLISHERS_AND_WRITERS.ALL,
        value: {
            publisher: null,
            writer: null,
        },
    },
    allMarkets: {
        id: 'market',
        label: locale.enUS.DETECTIONS.FILTERS.MARKETS.ALL,
        value: {
            fieldValue: null,
        },
    },
    allStationNames: {
        id: 'stationName',
        label: locale.enUS.DETECTIONS.FILTERS.STATION_NAMES.ALL,
        value: {
            fieldValue: null,
        },
    },
};

/**
 * The first value of each tuple here is a filter ID, which is independent of column IDs. Columns
 * can display more than one field in the data; filters can only map to a single field in the data.
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- see comment next to `episode: null` below
// @ts-ignore
export const preFilterConfigs: Map<string, PreFilterConfig> = new Map([
    [
        'duration',
        {
            id: 'duration',
            emptyOption: {
                id: 'detections.allDurations',
                label: locale.enUS.DETECTIONS.FILTERS.DURATIONS.ALL,
                value: {
                    duration: null,
                },
            },
            fieldValuePath: ['duration'],
            filterType: 'greaterThan',
            filterValuePath: ['value', 'duration'],
            filterValueType: 'number',
            urlParamKey: URL_PARAMS.DURATION_GREATER_THAN,
            renderType: 'dropdown',
            dropdownOptions: [
                {
                    id: 'detections.duration.greaterThan.3',
                    label: '> 3s',
                    value: {
                        duration: 3,
                    },
                },
                {
                    id: 'detections.duration.greaterThan.5',
                    label: '> 5s',
                    value: {
                        duration: 5,
                    },
                },
                {
                    id: 'detections.duration.greaterThan.9',
                    label: '> 9s',
                    value: {
                        duration: 9,
                    },
                },
                {
                    id: 'detections.duration.greaterThan.14',
                    label: '> 14s',
                    value: {
                        duration: 14,
                    },
                },
                {
                    id: 'detections.duration.greaterThan.24',
                    label: '> 24s',
                    value: {
                        duration: 24,
                    },
                },
            ],
        },
    ],
    [
        'confidence',
        {
            id: 'confidence',
            emptyOption: {
                id: 'detections.allConfidences',
                label: locale.enUS.DETECTIONS.FILTERS.CONFIDENCES.ALL,
                value: {
                    confidence: null,
                },
            },
            fieldValuePath: ['confidence'],
            filterType: 'confidenceGreaterThanOrEqualTo',
            filterValuePath: ['value', 'confidence'],
            urlParamKey: URL_PARAMS.CONFIDENCE,
            renderType: 'dropdown',
            dropdownOptions: [
                {
                    id: 'detections.confidence.low',
                    label: '>= Low',
                    value: {
                        confidence: 'LOW',
                    },
                },
                {
                    id: 'detections.confidence.medium',
                    label: '>= Medium',
                    value: {
                        confidence: 'MEDIUM',
                    },
                },
                {
                    id: 'detections.confidence.high',
                    label: 'High',
                    value: {
                        confidence: 'HIGH',
                    },
                },
            ],
        },
    ],
    [
        'title',
        {
            id: 'title',
            emptyOption: {
                id: 'detections.allTitles',
                label: locale.enUS.DETECTIONS.FILTERS.TITLES.ALL,
                value: {
                    show: null,
                },
            },
            fieldValuePath: ['title'],
            filterType: 'equals',
            filterValuePath: ['value', 'title'],
            urlParamKey: URL_PARAMS.TITLE,
            renderType: 'dropdown',
        },
    ],
    [
        'filename',
        {
            id: 'filename',
            emptyOption: {
                id: 'detections.allFilenames',
                label: locale.enUS.DETECTIONS.FILTERS.FILENAMES.ALL,
                value: {
                    show: null,
                },
            },
            fieldValuePath: ['filename'],
            filterType: 'equals',
            filterValuePath: ['value', 'filename'],
            urlParamKey: URL_PARAMS.FILENAME,
            renderType: 'dropdown',
        },
    ],
    [
        'channel',
        {
            id: 'channel',
            emptyOption: {
                id: 'detections.allChannels',
                label: locale.enUS.DETECTIONS.FILTERS.CHANNELS.ALL, // This reads "All Stations" in the UI
                value: {
                    show: null,
                },
            },
            fieldValuePath: ['channel'],
            filterType: 'equals',
            filterValuePath: ['value', 'channel'],
            urlParamKey: URL_PARAMS.STATION,
            renderType: 'dropdown',
        },
    ],
    [
        'country',
        {
            id: 'country',
            emptyOption: {
                id: 'detections.allCountries',
                label: locale.enUS.DETECTIONS.FILTERS.COUNTRIES.ALL,
                value: {
                    country: null,
                },
            },
            fieldValuePath: ['country'],
            filterType: 'equals',
            filterValuePath: ['value', 'country'],
            getDropdownOptionLabel: (
                data: DetectionsTableItem,
                fieldValue: string | number | undefined,
            ): ReactNode => {
                const country = data?.country ?? null;

                const countryCode = country
                    ? detectionCountryMap?.[country]
                    : null;

                if (country === null || countryCode === null) {
                    return fieldValue;
                }

                return `${country} (${countryCode})`;
            },
            urlParamKey: URL_PARAMS.COUNTRY,
            renderType: 'dropdown',
        },
    ],
    [
        'show',
        {
            id: 'show',
            emptyOption: {
                id: 'detections.allShows',
                label: locale.enUS.DETECTIONS.FILTERS.SHOWS.ALL,
                value: {
                    show: null,
                },
            },
            fieldValuePath: ['show'],
            filterType: 'equals',
            filterValuePath: ['value', 'show'],
            urlParamKey: URL_PARAMS.SHOW,
            renderType: 'dropdown',
        },
    ],
    [
        'programProductionCompany',
        {
            id: 'programProductionCompany',
            emptyOption: {
                id: 'detections.programProductionCompanies',
                label: locale.enUS.DETECTIONS.FILTERS
                    .PROGRAM_PRODUCTION_COMPANIES.ALL,
                value: {
                    episode: null,
                },
            },
            fieldValuePath: ['program_production_company'],
            filterType: 'equals',
            filterValuePath: ['value', 'program_production_company'],
            urlParamKey: URL_PARAMS.PROGRAM_PRODUCTION_COMPANY,
            renderType: 'dropdown',
        },
    ],
]);

export const defaultDateRangeOption = {
    label: locale.enUS.DATE_RANGES.PAST_7_DAYS,
    value: getDateRangeBefore(new Date(), { days: 7 }),
};
